// Shared look and helpers for the owner Reports section.

export const fmtNum = (v, dp = 2) =>
  Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: dp });

export const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

export const fmtDay = (v) =>
  v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

/** One cell, formatted by the column's declared type. */
export function formatValue(type, v) {
  if (v === null || v === undefined || v === '') return '—';
  switch (type) {
    case 'money': return `৳ ${fmtNum(v)}`;
    case 'points': return fmtNum(v);
    case 'number': return typeof v === 'number' ? fmtNum(v, 0) : String(v);
    case 'datetime': return fmtDateTime(v);
    default: return String(v);
  }
}

export const TONE = {
  success: '#15803d',
  danger: '#b91c1c',
  warning: '#b45309',
};

// Status words the reports emit, mapped to a pill colour.
export function statusTone(text) {
  const t = String(text || '').toLowerCase();
  if (/(approved|delivered|verified|active|credited|money in|income|settled|success)/.test(t)) return { bg: '#dcfce7', color: '#166534' };
  if (/(pending|partly|waiting|not verified)/.test(t)) return { bg: '#fef3c7', color: '#92400e' };
  if (/(blocked|rejected|money out|expense|fail|not delivered)/.test(t)) return { bg: '#fee2e2', color: '#991b1b' };
  if (/(admin|agent|manager|merchant)/.test(t)) return { bg: '#e0e7ff', color: '#3730a3' };
  return { bg: '#f3f4f6', color: '#374151' };
}

export const st = {
  page: { width: '87%', float: 'right', padding: '16px 22px 70px', boxSizing: 'border-box', color: '#111827' },
  crumbs: { fontSize: 12.5, color: '#6b7280', marginBottom: 6 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' },
  h1: { fontSize: 24, fontWeight: 800, margin: 0 },
  lede: { color: '#4b5563', fontSize: 14, margin: '4px 0 0', maxWidth: 760 },
  muted: { color: '#6b7280', fontSize: 12.5 },

  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, margin: '14px 0' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  cardTitle: { fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: '#374151' },

  chips: { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' },
  chip: { background: '#fff', border: '1px solid #d1d5db', borderRadius: 999, padding: '6px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' },
  chipOn: { background: '#111827', border: '1px solid #111827', borderRadius: 999, padding: '6px 13px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff' },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#4b5563' },
  input: { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box', width: '100%', background: '#fff', fontWeight: 400, color: '#111827' },

  btn: { background: '#2563eb', color: '#fff', border: '1px solid #2563eb', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 13.5, whiteSpace: 'nowrap' },
  btnDark: { background: '#111827', color: '#fff', border: '1px solid #111827', borderRadius: 8, padding: '8px 14px', fontWeight: 700, cursor: 'pointer', fontSize: 13.5, whiteSpace: 'nowrap' },
  btnLight: { background: '#fff', color: '#111827', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: 13.5, whiteSpace: 'nowrap' },

  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: 10, margin: '14px 0' },
  stat: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 2, minHeight: 84 },
  statLabel: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#6b7280', fontWeight: 700 },
  statValue: { fontSize: 22, fontWeight: 800, lineHeight: 1.2 },
  statHint: { fontSize: 11.5, color: '#6b7280' },

  tableWrap: { overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '9px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.03em', color: '#6b7280', whiteSpace: 'nowrap' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13, verticalAlign: 'top' },
  pill: { borderRadius: 999, padding: '2px 9px', fontSize: 11.5, fontWeight: 700, display: 'inline-block', whiteSpace: 'nowrap' },
  pager: { marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between' },
  notice: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '8px 12px', fontSize: 13, margin: '8px 0' },

  popover: { position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 30, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 12px 30px rgba(0,0,0,.12)', padding: 12, width: 260, maxHeight: 380, overflowY: 'auto' },
};
